/**
 * RNNoise Processor — AI noise removal using Mozilla RNNoise WASM
 * Processes audio files through the RNNoise neural network for speech denoising.
 * RNNoise works at 48kHz with 480-sample frames (10ms).
 * Uses VAD-adaptive mixing: less processing during speech (preserve naturalness),
 * more aggressive denoising during silence/noise.
 */
window.RNNoiseProcessor = (function() {
    var _module = null;
    var _loaded = false;
    var _lastError = null;  // last init error — surfaced to EngineWarmer telemetry via .lastError accessor
    var FRAME_SIZE = 480; // RNNoise fixed frame size (10ms at 48kHz)
    var SAMPLE_RATE = 48000;
    var BATCH_SIZE = 100;  // Frames per batch before yielding to UI (~0.25s at 48kHz)

    return {
        /**
         * Initialize RNNoise WASM module
         * @param {Function} onProgress - progress callback (0-1)
         * @returns {Promise<boolean>}
         */
        init: async function(onProgress) {
            if (_loaded) return true;
            if (onProgress) onProgress(0);
            _lastError = null;

            try {
                if (typeof createRNNWasmModule === 'undefined') {
                    throw new Error('RNNoise WASM loader not found');
                }

                _module = await createRNNWasmModule({
                    locateFile: function(path) {
                        return '/vendor/rnnoise/' + path;
                    }
                });

                _loaded = true;
                if (onProgress) onProgress(1);
                return true;
            } catch(e) {
                console.error('RNNoise init failed:', e);
                _lastError = e;
                return false;
            }
        },

        /**
         * Last init error (null on first run, after a successful init, or if
         * never initialized). EngineWarmer registrations read this to enrich
         * the `engine_warm_failed` telemetry payload — pre-fix the payload
         * had no `err` field when init resolved false (the catch path),
         * leaving prod with 4 silent failures and no diagnosis trail.
         */
        get lastError() { return _lastError; },

        /**
         * Check if module is loaded
         */
        isLoaded: function() {
            return _loaded;
        },

        /**
         * Process audio buffer through RNNoise with VAD-adaptive mixing
         * @param {AudioBuffer} audioBuffer - input audio (any sample rate)
         * @param {number} strength - base wet/dry mix 0.0-1.0
         * @param {Function} onProgress - progress callback (0-1)
         * @returns {Promise<{channels: Float32Array[], sampleRate: number, numberOfChannels: number}>}
         */
        process: async function(audioBuffer, strength, onProgress) {
            if (!_loaded) throw new Error('RNNoise not initialized');

            var mod = _module;
            var channels = audioBuffer.numberOfChannels;

            // Step 1: Resample to 48kHz if needed
            if (onProgress) onProgress(0.05);
            var buffer48k;
            if (audioBuffer.sampleRate !== SAMPLE_RATE) {
                var newLength = Math.ceil(audioBuffer.duration * SAMPLE_RATE);
                var offlineCtx = new OfflineAudioContext(channels, newLength, SAMPLE_RATE);
                var source = offlineCtx.createBufferSource();
                source.buffer = audioBuffer;
                source.connect(offlineCtx.destination);
                source.start();
                buffer48k = await offlineCtx.startRendering();
            } else {
                buffer48k = audioBuffer;
            }
            if (onProgress) onProgress(0.1);

            // Step 2: Allocate WASM buffers
            var inputPtr = mod._malloc(FRAME_SIZE * 4);
            var outputPtr = mod._malloc(FRAME_SIZE * 4);

            var processedChannels = [];

            // Step 3: Process each channel with VAD-adaptive mixing
            for (var ch = 0; ch < channels; ch++) {
                var state = mod._rnnoise_create();
                var channelData = buffer48k.getChannelData(ch);
                var totalSamples = channelData.length;
                // rnnoise_process_frame answers ONE frame late (its analysis
                // window overlaps the previous frame; rnnoise-realtime.js
                // subtracts the same 480). Measured here 2026-09-24: the
                // output cross-correlated with the input at 479. Blending that
                // late wet with the on-time dry below was a comb filter —
                // notches every 100 Hz on every voice. Read the output one
                // frame ahead, flush the last frame with silence.
                var DELAY = FRAME_SIZE;
                var totalFrames = Math.ceil((totalSamples + DELAY) / FRAME_SIZE);
                var output = new Float32Array(totalSamples);
                var smoothVad = 0.5; // smoothed VAD to prevent pumping

                for (var f = 0; f < totalFrames; f++) {
                    var offset = f * FRAME_SIZE;

                    // Copy input frame to WASM memory (scaled to int16 range)
                    var heapF32 = mod.HEAPF32;
                    var inIdx = inputPtr >> 2;
                    for (var j = 0; j < FRAME_SIZE; j++) {
                        var idx = offset + j;
                        heapF32[inIdx + j] = (idx < totalSamples ? channelData[idx] : 0) * 32768;
                    }

                    // Process frame — returns VAD probability (0.0=noise, 1.0=speech)
                    var vad = mod._rnnoise_process_frame(state, outputPtr, inputPtr);

                    // Smooth VAD to prevent abrupt changes (pumping)
                    smoothVad = smoothVad * 0.8 + vad * 0.2;

                    // VAD-adaptive mixing:
                    // - During speech (high VAD): use less RNNoise to preserve naturalness
                    // - During silence (low VAD): use more RNNoise to aggressively remove noise
                    var adaptiveStrength;
                    if (smoothVad > 0.6) {
                        // Speech detected — scale down to preserve natural voice
                        adaptiveStrength = strength * 0.8;
                    } else if (smoothVad < 0.15) {
                        // Pure noise/silence — push harder
                        adaptiveStrength = Math.min(strength * 1.25, 1.0);
                    } else {
                        // Transition zone — smooth interpolation
                        var t = (smoothVad - 0.15) / 0.45;
                        adaptiveStrength = strength * (1.25 - t * 0.45);
                    }

                    // Copy output with adaptive wet/dry mix — output sample j of
                    // this call answers input position offset + j − DELAY.
                    var outIdx = outputPtr >> 2;
                    var at = offset - DELAY;
                    var remaining = 0;
                    for (var j = 0; j < FRAME_SIZE; j++) {
                        var o = at + j;
                        if (o < 0 || o >= totalSamples) continue;
                        var dry = channelData[o];
                        var wet = heapF32[outIdx + j] / 32768;
                        output[o] = wet * adaptiveStrength + dry * (1 - adaptiveStrength);
                        remaining++;
                    }

                    // Smooth frame boundaries (4 samples = 0.08ms, imperceptible)
                    if (at > 0 && remaining > 4) {
                        for (var j = 0; j < 4; j++) {
                            var t = (j + 1) / 5;
                            output[at + j] = output[at - 1] * (1 - t) + output[at + j] * t;
                        }
                    }

                    // Yield to UI thread periodically
                    if (f % BATCH_SIZE === 0 && f > 0) {
                        await new Promise(function(r) { setTimeout(r, 0); });
                        if (onProgress) {
                            var channelProg = f / totalFrames;
                            var totalProg = 0.1 + (ch / channels + channelProg / channels) * 0.75;
                            onProgress(totalProg);
                        }
                    }
                }

                mod._rnnoise_destroy(state);
                processedChannels.push(output);
            }

            // Cleanup WASM buffers
            mod._free(inputPtr);
            mod._free(outputPtr);

            if (onProgress) onProgress(0.85);

            return {
                channels: processedChannels,
                sampleRate: SAMPLE_RATE,
                numberOfChannels: channels
            };
        },

        /**
         * ONE recording fed in pieces, mono, 48 kHz (/speech-restorer streams a
         * recording of any length in 30-s blocks). Separate process() calls
         * start a fresh network each time, and RNNoise is recurrent: its
         * output depends on the whole history, so blocks processed apart never
         * meet at the seam — measured 2026-09-25, a 2-s and even a 6-s warm-up
         * left −34…−45 dB steps at every seam. The stream keeps the network,
         * the VAD smoothing and the one-frame delay alive across pieces, and is
         * sample-identical to process() on the whole recording.
         *
         *   var st = RNNoiseProcessor.openStream(strength);
         *   out = st.process(float32Piece);   // the samples that are final
         *   out = st.finish();                // the rest
         */
        openStream: function(strength) {
            if (!_loaded) throw new Error('RNNoise not initialized');
            var mod = _module;
            var st = mod._rnnoise_create();
            var inPtr = mod._malloc(FRAME_SIZE * 4), outPtr = mod._malloc(FRAME_SIZE * 4);
            var smoothVad = 0.5, frames = 0, total = 0, done = false;
            var pend = new Float32Array(0);             // input not yet framed
            var prevIn = new Float32Array(FRAME_SIZE);  // input of the previous frame = the dry of this frame's output
            var lastOut = 0;
            function cat(a, b) { var o = new Float32Array(a.length + b.length); o.set(a, 0); o.set(b, a.length); return o; }
            // One frame, exactly as process() does it. `limit` = the recording's
            // length once known (finish), else Infinity.
            function frame(input, limit, sink) {
                var heapF32 = mod.HEAPF32, inIdx = inPtr >> 2;
                for (var j = 0; j < FRAME_SIZE; j++) heapF32[inIdx + j] = input[j] * 32768;
                var vad = mod._rnnoise_process_frame(st, outPtr, inPtr);
                smoothVad = smoothVad * 0.8 + vad * 0.2;
                var a;
                if (smoothVad > 0.6) a = strength * 0.8;
                else if (smoothVad < 0.15) a = Math.min(strength * 1.25, 1.0);
                else { var t = (smoothVad - 0.15) / 0.45; a = strength * (1.25 - t * 0.45); }
                heapF32 = mod.HEAPF32;
                var outIdx = outPtr >> 2, at = frames * FRAME_SIZE - FRAME_SIZE;
                var region = new Float32Array(FRAME_SIZE), n = 0;
                for (var k = 0; k < FRAME_SIZE; k++) {
                    var o = at + k;
                    if (o < 0 || o >= limit) continue;
                    region[k] = (heapF32[outIdx + k] / 32768) * a + prevIn[k] * (1 - a);
                    n++;
                }
                if (at > 0 && n > 4) {
                    for (var q = 0; q < 4; q++) { var w = (q + 1) / 5; region[q] = lastOut * (1 - w) + region[q] * w; }
                }
                if (n > 0) { lastOut = region[n - 1]; sink.push(region.subarray(0, n)); }
                prevIn = new Float32Array(input);
                frames++;
            }
            function join(parts) { var len = 0, i; for (i = 0; i < parts.length; i++) len += parts[i].length; var o = new Float32Array(len), p = 0; for (i = 0; i < parts.length; i++) { o.set(parts[i], p); p += parts[i].length; } return o; }
            return {
                process: function (x) {
                    if (done) throw new Error('RNNoise stream already finished');
                    total += x.length;
                    pend = cat(pend, x);
                    var out = [];
                    var off = 0;
                    while (pend.length - off >= FRAME_SIZE) { frame(pend.subarray(off, off + FRAME_SIZE), Infinity, out); off += FRAME_SIZE; }
                    pend = pend.slice(off);
                    return join(out);
                },
                finish: function () {
                    if (done) return new Float32Array(0);
                    done = true;
                    var out = [], totalFrames = Math.ceil((total + FRAME_SIZE) / FRAME_SIZE);
                    while (frames < totalFrames) {
                        var input = new Float32Array(FRAME_SIZE);
                        input.set(pend.subarray(0, Math.min(FRAME_SIZE, pend.length)));
                        pend = pend.slice(Math.min(FRAME_SIZE, pend.length));
                        frame(input, total, out);
                    }
                    mod._rnnoise_destroy(st);
                    mod._free(inPtr); mod._free(outPtr);
                    return join(out);
                }
            };
        },

        /**
         * Reinitialize (for error recovery)
         */
        reinit: async function() {
            _module = null;
            _loaded = false;
            return this.init();
        }
    };
})();

/**
 * Create a WAV blob from processed audio data (32-bit float)
 * @param {{channels: Float32Array[], sampleRate: number, numberOfChannels: number}} data
 * @returns {Blob}
 */
window.createWavBlob = function(data) {
    var numChannels = data.numberOfChannels;
    var sampleRate = data.sampleRate;
    var channelData = data.channels;
    var numSamples = channelData[0].length;

    var bytesPerSample = 2; // 16-bit PCM
    var blockAlign = numChannels * bytesPerSample;
    var byteRate = sampleRate * blockAlign;
    var dataSize = numSamples * blockAlign;
    var headerSize = 44;
    var totalSize = headerSize + dataSize;

    var buffer = new ArrayBuffer(totalSize);
    var view = new DataView(buffer);

    // RIFF header
    view.setUint8(0, 0x52); view.setUint8(1, 0x49); view.setUint8(2, 0x46); view.setUint8(3, 0x46); // "RIFF"
    view.setUint32(4, totalSize - 8, true);
    view.setUint8(8, 0x57); view.setUint8(9, 0x41); view.setUint8(10, 0x56); view.setUint8(11, 0x45); // "WAVE"

    // fmt chunk
    view.setUint8(12, 0x66); view.setUint8(13, 0x6D); view.setUint8(14, 0x74); view.setUint8(15, 0x20); // "fmt "
    view.setUint32(16, 16, true);    // chunk size
    view.setUint16(20, 1, true);     // PCM format
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bytesPerSample * 8, true);

    // data chunk
    view.setUint8(36, 0x64); view.setUint8(37, 0x61); view.setUint8(38, 0x74); view.setUint8(39, 0x61); // "data"
    view.setUint32(40, dataSize, true);

    // Write interleaved 16-bit PCM samples
    var pos = 44;
    for (var i = 0; i < numSamples; i++) {
        for (var ch = 0; ch < numChannels; ch++) {
            var sample = channelData[ch][i];
            sample = sample < -1 ? -1 : (sample > 1 ? 1 : sample);
            view.setInt16(pos, sample < 0 ? sample * 32768 : sample * 32767, true);
            pos += 2;
        }
    }

    return new Blob([buffer], { type: 'audio/wav' });
};
